import { z } from 'zod';
import { IsoTimestampSchema, UuidSchema } from './common';

export const USER_ROLES = ['superadmin', 'admin', 'viewer'] as const;
export type UserRole = (typeof USER_ROLES)[number];

export const UserRoleSchema = z.enum(USER_ROLES);

/**
 * Maximum number of applications a user can pin to the top of the Overview "App health" card,
 * counted per environment.
 */
export const OVERVIEW_PINNED_APPS_MAX = 3;

const PinnedAppIdsSchema = z.array(UuidSchema).max(OVERVIEW_PINNED_APPS_MAX);

/**
 * Pinned apps are stored per environment: an application row exists once per environment with its
 * own id, so a single flat list made development and production fight over the same slots.
 */
export const OverviewPinnedAppsSchema = z.object({
  development: PinnedAppIdsSchema.optional(),
  production: PinnedAppIdsSchema.optional(),
});

export const UserPreferencesSchema = z.object({
  overviewPinnedApps: OverviewPinnedAppsSchema.optional(),
  /** @deprecated Superseded by `overviewPinnedApps`; only read as a fallback for pre-existing rows. */
  overviewPinnedAppIds: PinnedAppIdsSchema.optional(),
});

export const UserSchema = z.object({
  id: UuidSchema,
  email: z.string().email(),
  role: UserRoleSchema,
  preferences: UserPreferencesSchema,
  createdAt: IsoTimestampSchema,
});

export const UpdatePreferencesSchema = UserPreferencesSchema;

export const CreateUserSchema = z.object({
  email: z.string().email().max(255),
  password: z.string().min(8).max(200),
  role: UserRoleSchema,
});

export const UpdateUserSchema = z
  .object({
    email: z.string().email().max(255).optional(),
    role: UserRoleSchema.optional(),
  })
  .refine((v) => v.email !== undefined || v.role !== undefined, {
    message: 'At least one of email or role must be provided',
  });

export const ChangePasswordSchema = z.object({
  currentPassword: z.string().min(1).max(200),
  newPassword: z.string().min(8).max(200),
});

export const ResetPasswordSchema = z.object({
  newPassword: z.string().min(8).max(200),
});

export type User = z.infer<typeof UserSchema>;
export type UserPreferences = z.infer<typeof UserPreferencesSchema>;
export type UpdatePreferences = z.infer<typeof UpdatePreferencesSchema>;
export type CreateUser = z.infer<typeof CreateUserSchema>;
export type UpdateUser = z.infer<typeof UpdateUserSchema>;
export type ChangePassword = z.infer<typeof ChangePasswordSchema>;
export type ResetPassword = z.infer<typeof ResetPasswordSchema>;
